import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { apiClient, onSessionExpired, useApiBusy } from './api';
import { EMPTY_FILES, keepListing, keptListing, normalizeFiles } from './lib/files';
import { ScreenStateContext } from './lib/screenState';
import { DEFAULT_PLACE, NavContext, nextPlace, parsePlace, placeUrl } from './lib/navigation';
import { fitNavDensity } from './lib/navDensity';
import { isAdmin, roleLabel } from './lib/roles';
import { AdminIcon, AuditIcon, ClientsIcon, GraphIcon, LogoutIcon, RepublishIcon, ToastContainer, UpdatesIcon } from './components/ui';
import LoginStage from './login/LoginStage';
import McpAccess from './panels/McpAccess';
import Dashboard from './dashboard/Dashboard';
import HelpPage from './help/HelpPage';
import PrerequisitesPanel from './prerequisites/PrerequisitesPanel';
import ClientsPanel from './panels/ClientsPanel';
import AuditLogPanel, { AUDIT_FILTERS_KEY, NO_FILTERS } from './panels/AuditLogPanel';
import AdminPanel from './panels/AdminPanel';
import RepublishPanel from './panels/RepublishPanel';

const TOAST_MS = 8000;
// Los listados de las últimas versiones vistas que se guardan en memoria (cada uno pesa varios MB): volver a una se ve al
// instante y se pide de nuevo por detrás. Con más, se suelta el que hace más que no se mira.
const KEPT_LISTINGS = 3;

// Pestañas y quién las ve. El servidor valida lo mismo en cada pedido.
const everyone = () => true;
const TABS = [
    // Republicación va primera en el menú, pero la pantalla en la que se entra sigue siendo Updates.
    { id: 'republish', label: 'Republicación', Icon: RepublishIcon, visible: everyone },
    { id: 'dashboard', label: 'Updates', Icon: UpdatesIcon, visible: everyone },
    { id: 'prerequisites', label: 'Prerrequisitos', Icon: GraphIcon, visible: everyone },
    { id: 'clients', label: 'Clientes', Icon: ClientsIcon, visible: everyone },
    // El publicador ve sólo los registros de updates: lo filtra el servidor.
    { id: 'audit', label: 'Auditoría', Icon: AuditIcon, visible: everyone },
    { id: 'admin', label: 'Administración', Icon: AdminIcon, visible: isAdmin },
];

// La vuelta del ingreso con Microsoft (/api/auth/microsoft/callback) trae en el fragmento de la dirección el token
// o por qué no se pudo entrar. Se guarda el token, se borra el fragmento y se devuelve el motivo (o '').
export const takeLoginFragment = () => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    if (!params.has('token') && !params.has('login_error')) return '';
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (params.get('token')) localStorage.setItem('accessToken', params.get('token'));
    return params.get('login_error') || '';
};

// Un link a Updates con lo elegido (lo arma el MCP de sólo lectura): `?q=<update>&versions=13.0.0.0,14.0.0.0&clients=TEC,PCM`.
// La primera versión es la del tablero. Se saca de la dirección y queda en sessionStorage hasta usarlo: así sobrevive
// al ingreso con Microsoft, que sale de la página y vuelve sin la query. Devuelve null si no hay link.
const LINK_KEY = 'updatesLink';
export const takeUpdatesLink = () => {
    const params = new URLSearchParams(window.location.search);
    const list = (name) => (params.get(name) || '').split(',').map(item => item.trim()).filter(Boolean);
    if (!params.has('tab') && ['q', 'versions', 'clients'].some(name => params.has(name))) {
        const link = { q: (params.get('q') || '').trim(), versions: list('versions'), clients: list('clients') };
        window.history.replaceState(null, '', window.location.pathname + window.location.hash);
        try { sessionStorage.setItem(LINK_KEY, JSON.stringify(link)); } catch { /* sin storage: vale para esta carga */ }
        return link;
    }
    try { return JSON.parse(sessionStorage.getItem(LINK_KEY)) || null; } catch { return null; }
};

// El lugar con el que se abre la app: el de la dirección (un link copiado, una recarga) o, si la vuelta de Microsoft lo perdió,
// el que quedó guardado. Se guarda hasta tener sesión.
const PLACE_KEY = 'startPlace';
export const takeStartPlace = () => {
    const fromUrl = parsePlace(window.location.search);
    try {
        if (fromUrl) sessionStorage.setItem(PLACE_KEY, window.location.search);
        return { ...DEFAULT_PLACE, ...(fromUrl || parsePlace(sessionStorage.getItem(PLACE_KEY) || '')) };
    } catch { return { ...DEFAULT_PLACE, ...fromUrl }; }
};

export default function App() {
    // Antes que nada: si se vuelve de Microsoft, su token ya queda guardado para el chequeo de la sesión.
    const [loginError] = useState(takeLoginFragment);
    const [updatesLink] = useState(takeUpdatesLink);
    const [user, setUser] = useState(null);
    const [checkingSession, setCheckingSession] = useState(true);
    const [catalog, setCatalog] = useState({ versions: [], clients: [], users: [] });
    // Los listados ya normalizados de las últimas versiones vistas, de la más vieja a la más nueva. Mientras la versión elegida no tiene el suyo
    // (la primera vez que se la ve) las columnas dicen "Cargando": nunca se muestra el de la versión anterior.
    const [listings, setListings] = useState([]);
    // Dónde está la persona (pantalla, versión y lo que se lee como página dentro de cada una): lo dice la dirección y cada
    // cambio suma una entrada al historial del navegador. Al entrar no hay ninguna versión elegida y no se pide ningún update:
    // `selectedVersion` queda vacío hasta que el usuario elige, y vuelve a quedar vacío si quita la última.
    const [place, setPlace] = useState(takeStartPlace);
    const placeRef = useRef(place);
    const { tab: activeTab, version: selectedVersion } = place;
    // La pantalla desde la que se abrió la Ayuda: "Volver" vuelve ahí.
    const [helpFrom, setHelpFrom] = useState('dashboard');
    const [connectionError, setConnectionError] = useState(false);
    const [mcpOpen, setMcpOpen] = useState(false);
    // Hasta que llegan el catálogo y los archivos de la versión elegida, las columnas dicen "Cargando" en vez de "no hay nada".
    const [catalogLoaded, setCatalogLoaded] = useState(false);
    const busy = useApiBusy();
    const [toasts, setToasts] = useState([]);
    // Lo que el usuario eligió en cada pantalla; se conserva al cambiar de pestaña y se vacía al cerrar sesión.
    const screenState = useRef(new Map());
    // La versión cuyo listado se pidió último: una respuesta que llega tarde (de la versión anterior, de una que ya se
    // quitó, o después de cerrar sesión) no se usa.
    const wantedVersion = useRef('');

    const closeToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);
    const addToast = useCallback((message, type = 'info') => {
        const id = Math.random().toString(36).slice(2, 9);
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => closeToast(id), TOAST_MS);
    }, [closeToast]);

    // Ir a otro lugar: suma una entrada al historial si cambia la dirección (o reemplaza la actual con `replace`, para lo que
    // no es una navegación de la persona). Atrás y Adelante vuelven por `popstate`, sin pasar por acá.
    const go = useCallback((patch, { replace = false } = {}) => {
        let from = placeRef.current;
        // Lo escrito en la búsqueda de Updates y no confirmado (Enter o salir del campo) queda en la entrada que se deja.
        const typed = (screenState.current.get('dashboard.search') ?? '').trim();
        if (from.tab === 'dashboard' && !('q' in patch) && typed !== from.q) {
            from = { ...from, q: typed };
            window.history.replaceState(null, '', placeUrl(from));
        }
        const next = nextPlace(from, patch);
        placeRef.current = next;
        setPlace(next);
        if (placeUrl(next) !== window.location.pathname + window.location.search) window.history[replace ? 'replaceState' : 'pushState'](null, '', placeUrl(next));
    }, []);
    useEffect(() => {
        const back = () => { placeRef.current = { ...placeRef.current, ...(parsePlace(window.location.search) || DEFAULT_PLACE) }; setPlace(placeRef.current); };
        window.addEventListener('popstate', back);
        return () => window.removeEventListener('popstate', back);
    }, []);
    const navigation = useMemo(() => ({ place, go }), [place, go]);
    const setSelectedVersion = useCallback((version) => go({ version }), [go]);

    // La sesión se cierra pero el lugar queda en la dirección: al volver a entrar se abre ahí.
    const endSession = useCallback(() => {
        localStorage.removeItem('accessToken');
        // Sesión nueva, contexto nuevo: se suelta todo lo elegido.
        screenState.current.clear(); setHelpFrom('dashboard');
        setCatalogLoaded(false); setListings([]); wantedVersion.current = '';
        setUser(null);
    }, []);
    // Con sesión se deja la dirección del lugar, también cuando se abrió por un link o se volvió de Microsoft.
    const openSession = useCallback(() => {
        try { sessionStorage.removeItem(PLACE_KEY); } catch { /* nada que limpiar */ }
        window.history.replaceState(null, '', placeUrl(placeRef.current));
    }, []);
    // "Salir" es de la persona: el próximo ingreso arranca de cero, en la entrada.
    const logout = useCallback(() => { endSession(); go(DEFAULT_PLACE, { replace: true }); try { sessionStorage.removeItem(PLACE_KEY); } catch { /* nada que limpiar */ } }, [endSession, go]);
    // "Historial" de un update o de un cliente: abre Auditoría con ese único filtro puesto.
    const openHistory = useCallback((filter) => {
        screenState.current.set(AUDIT_FILTERS_KEY, { ...NO_FILTERS, ...filter });
        go({ tab: 'audit' });
    }, [go]);
    // El link se usa una vez, al abrir la sesión y antes de que Updates se monte: Updates lee lo elegido al montarse.
    // Queda tildado el update del link; publicar sigue siendo cosa de la persona, con su confirmación.
    const linkUsed = useRef(false);
    const openUpdatesLink = useCallback(() => {
        if (!updatesLink || linkUsed.current) return;
        linkUsed.current = true;
        try { sessionStorage.removeItem(LINK_KEY); } catch { /* nada que limpiar */ }
        const { q, versions, clients } = updatesLink;
        const [board = '', ...extra] = versions;
        [['dashboard.clients', clients], ['dashboard.search', q], ['dashboard.onlyPending', true], ['dashboard.extraVersions', extra],
            ['dashboard.files', q ? [q] : []], ['dashboard.filesVersion', board]].forEach(([key, value]) => screenState.current.set(key, value));
        go({ tab: 'dashboard', version: board, q }, { replace: true });
    }, [updatesLink, go]);
    const login = (token, userData) => { localStorage.setItem('accessToken', token); openUpdatesLink(); openSession(); setUser(userData); };
    // Un 401 en cualquier pedido significa sesión vencida o usuario dado de baja: se vuelve al login.
    const handleError = useCallback((e) => { if (e.response?.status === 401) endSession(); else setConnectionError(true); }, [endSession]);
    // Lo mismo para los pedidos que no pasan por `handleError` (publicar, ignorar, la consulta del cartel de confirmación).
    useEffect(() => onSessionExpired(endSession), [endSession]);

    useEffect(() => {
        const verify = async () => {
            if (localStorage.getItem('accessToken')) {
                try { const profile = (await apiClient.get('/api/profile')).data; openUpdatesLink(); openSession(); setUser(profile); } catch (e) { endSession(); }
            }
            setCheckingSession(false);
        };
        verify();
    }, [endSession, openSession, openUpdatesLink]);

    const loadCatalog = useCallback(async () => {
        if (!user) return;
        try {
            const [versions, clients, users] = await Promise.all([
                apiClient.get('/api/versions'), apiClient.get('/api/clients'),
                isAdmin(user) ? apiClient.get('/api/users') : Promise.resolve({ data: [] })]);
            setCatalog({ versions: versions.data, clients: clients.data, users: users.data });
            setCatalogLoaded(true);
            setConnectionError(false);
        } catch (e) { handleError(e); }
    }, [user, handleError]);

    const loadFiles = useCallback(async (version) => {
        wantedVersion.current = version;
        if (!version) return;
        try {
            const data = normalizeFiles((await apiClient.get(`/api/files/${version}`)).data);
            if (wantedVersion.current !== version) return;
            setListings(kept => keepListing(kept, version, data, KEPT_LISTINGS));
            setConnectionError(false);
        } catch (e) {
            // 409: la versión se desactivó mientras estaba elegida. Se vuelve a pedir el catálogo, que ya no la trae.
            if (e.response?.status === 409) loadCatalog(); else handleError(e);
        }
    }, [handleError, loadCatalog]);

    useEffect(() => { loadCatalog(); }, [loadCatalog]);
    useEffect(() => { if (user) loadFiles(selectedVersion); }, [user, selectedVersion, loadFiles]);
    // Una versión que dejó de ofrecerse (un administrador la desactivó o la eliminó) se suelta: la pantalla queda sin versión elegida.
    useEffect(() => {
        if (catalogLoaded && selectedVersion && !catalog.versions.some(v => v.id === selectedVersion)) go({ version: '' }, { replace: true });
    }, [catalogLoaded, catalog.versions, selectedVersion, go]);
    // Después de una acción: lo guardado de las otras versiones se suelta (publicar o subir puede haberlas cambiado) y el
    // listado de la elegida se pide de nuevo, sin dejar de mostrar el que hay hasta que llega.
    const reload = useCallback(() => {
        setListings(kept => kept.filter(([version]) => version === selectedVersion));
        loadCatalog(); loadFiles(selectedVersion);
    }, [loadCatalog, loadFiles, selectedVersion]);

    // El menú muestra siempre los íconos: si las secciones no entran se achica el texto y, si tampoco, quedan sólo los
    // íconos. Se vuelve a medir cuando cambia el lugar disponible (la ventana) o las secciones (otro usuario).
    const navRef = useRef(null);
    useLayoutEffect(() => {
        const nav = navRef.current;
        if (!nav) return undefined;
        const fit = () => fitNavDensity(nav);
        fit();
        document.fonts?.ready.then(fit);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(fit);
        observer.observe(nav);
        return () => observer.disconnect();
    }, [user, checkingSession]);

    if (checkingSession) return (
        <div role="status" className="h-screen flex flex-col items-center justify-center gap-4 bg-chrome text-sm text-chrome-muted">
            <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="" width="56" height="56" />
            <span className="flex items-center gap-2"><span className="zaf-spinner" />Cargando Update Manager…</span>
        </div>
    );
    if (!user) return <LoginStage onLogin={login} initialMessage={loginError} />;

    const openHelp = () => { if (activeTab !== 'help') setHelpFrom(activeTab); go({ tab: 'help' }); };
    // Desde una fila de Clientes: Updates abre en esa versión con ese cliente elegido y en "Para liberar", que es
    // donde se ve qué le falta. Lo que había elegido en Updates (otros clientes, otras versiones, una búsqueda)
    // se reemplaza: si no, la lista mostraría otra cosa.
    const seeInUpdates = (version, client) => {
        [['dashboard.clients', [client]], ['dashboard.onlyPending', true], ['dashboard.search', ''], ['dashboard.extraVersions', []]]
            .forEach(([key, value]) => screenState.current.set(key, value));
        go({ tab: 'dashboard', version, q: '' });
    };
    // Lo desactualizado se corrige en Republicación: abre ahí, con la lista entera (de todas las versiones) a la vista.
    const seeInRepublish = () => { screenState.current.set('republish.search', ''); go({ tab: 'republish' }); };

    const shownFiles = keptListing(listings, selectedVersion);
    const files = shownFiles || EMPTY_FILES;
    const loading = !catalogLoaded || (Boolean(selectedVersion) && !shownFiles);
    const shared = { user, versions: catalog.versions, clients: catalog.clients, files, loading, selectedVersion, onVersionChange: setSelectedVersion, reload, addToast };
    const tabs = TABS.filter(tab => tab.visible(user));
    return (
        <div className="h-screen flex flex-col bg-chrome font-sans text-slate-800">
            <ToastContainer toasts={toasts} onClose={closeToast} />
            {/* Encabezado con la navegación arriba: logo y título, secciones, y a la derecha el usuario, su rol y la salida.
                Cada sección lleva siempre su ícono; el nombre se achica o se oculta según el lugar (`fitNavDensity`). */}
            <header className="flex-shrink-0 flex items-center gap-4 h-14 px-6 bg-chrome-bar text-chrome-text border-b border-white/10">
                <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="Zafiro" width="28" height="28" className="flex-shrink-0" />
                <h1 className="flex-shrink-0 text-base font-semibold tracking-tight text-white whitespace-nowrap">Update Manager</h1>
                <nav ref={navRef} className="zaf-rail flex-grow min-w-0 flex gap-1 overflow-x-auto">
                    {tabs.map(({ id, label, Icon }) => (
                        <button key={id} type="button" onClick={() => go({ tab: id })} aria-current={activeTab === id ? 'page' : undefined} aria-label={label} title={label}
                            className={`zaf-rail-tab flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium whitespace-nowrap transition-colors ${activeTab === id ? 'bg-zafiro-600/20 border-zafiro-600/50 text-white shadow-lg shadow-zafiro-600/30' : 'border-transparent text-chrome-text/85 hover:bg-white/5 hover:text-white'}`}>
                            <span className="inline-flex flex-shrink-0"><Icon size={18} /></span><span className="zaf-rail-label">{label}</span>
                        </button>
                    ))}
                </nav>
                {/* Aviso de carga: mientras hay un pedido en curso se ve la barra y lo dice el encabezado. Su lugar está
                    reservado para que el menú no cambie de tamaño con cada pedido; salvo en pantallas anchas, es sólo el indicador. */}
                <span role="status" aria-live="polite" className="flex-shrink-0 flex items-center gap-2 w-4 2xl:w-28 text-xs font-medium text-glow whitespace-nowrap">{busy && <><span className="zaf-spinner flex-shrink-0" title="Trabajando…" /><span className="sr-only 2xl:not-sr-only">Trabajando…</span></>}</span>
                {/* La ayuda no es una sección más del menú: es un enlace a la derecha, para todos los tipos de usuario. */}
                <button type="button" onClick={openHelp} aria-current={activeTab === 'help' ? 'page' : undefined}
                    className={`flex-shrink-0 text-sm hover:text-white hover:underline ${activeTab === 'help' ? 'text-white underline' : 'text-chrome-muted'}`}>Ayuda</button>
                {/* El token personal del MCP: lo tiene cualquier usuario, como la Ayuda. */}
                <button type="button" onClick={() => setMcpOpen(true)} title="Tu token para consultar el Update Manager desde Claude o Cursor"
                    className="flex-shrink-0 text-sm text-chrome-muted hover:text-white hover:underline">MCP</button>
                <span className="flex-shrink-0 text-sm text-chrome-text">{user.username}</span>
                <span className="flex-shrink-0 px-2 py-0.5 rounded bg-chrome-raised text-xs font-medium text-glow whitespace-nowrap">{roleLabel(user.role)}</span>
                <button type="button" onClick={logout} className="flex-shrink-0 flex items-center gap-1.5 text-sm text-chrome-muted hover:text-white"><LogoutIcon size={16} />Salir</button>
            </header>
            {mcpOpen && <McpAccess addToast={addToast} onClose={() => setMcpOpen(false)} />}
            <div className="zaf-main relative flex-grow min-h-0 flex flex-col">
                <div className={`zaf-busy z-20 ${busy ? 'zaf-busy--on' : ''}`} />
                {connectionError && <p role="alert" className="flex-shrink-0 px-6 py-2 bg-red-50 text-sm text-red-700 border-b border-red-200">No se pudo conectar con el servidor.</p>}
                <main aria-busy={busy} className="flex-grow min-h-0">
                    <ScreenStateContext.Provider value={screenState.current}>
                    <NavContext.Provider value={navigation}>
                    {activeTab === 'republish' && <RepublishPanel {...shared} />}
                    {activeTab === 'dashboard' && <Dashboard {...shared} onOpenPrerequisites={(update) => go({ tab: 'prerequisites', update })} onOpenHelp={openHelp} onOpenHistory={(name) => openHistory({ update: name })} />}
                    {activeTab === 'help' && <HelpPage backLabel={TABS.find(tab => tab.id === helpFrom)?.label || 'Updates'} onBack={() => go({ tab: tabs.some(tab => tab.id === helpFrom) ? helpFrom : 'dashboard' })} />}
                    {activeTab === 'prerequisites' && <PrerequisitesPanel {...shared} />}
                    {activeTab === 'clients' && <ClientsPanel versions={catalog.versions} clients={catalog.clients} initialVersion={selectedVersion} addToast={addToast} onSeeInUpdates={seeInUpdates} onSeeInRepublish={seeInRepublish} onOpenHistory={(name) => openHistory({ client: name })} />}
                    {activeTab === 'audit' && <AuditLogPanel versions={catalog.versions} addToast={addToast} updatesOnly={!isAdmin(user)} />}
                    {activeTab === 'admin' && isAdmin(user) && <AdminPanel versions={catalog.versions} clients={catalog.clients} users={catalog.users} reload={reload} addToast={addToast} currentUser={user.username} />}
                    </NavContext.Provider>
                    </ScreenStateContext.Provider>
                </main>
            </div>
        </div>
    );
}
